import React, { useMemo, useState, useEffect } from 'react';
import { Row, Col } from 'react-bootstrap';
import Text from 'v3/components/Text';
import RouteMachineMap from 'v3/components/Map/LeafletRouteMachine/Map';
import { useSnackbar } from 'v3/components/Snackbar';
import { useParams } from 'react-router-dom';
import { formatAddress } from 'v3/utils/formatter';
import { formatNumber } from 'utils/formatter';
import api from 'services/api';
import Button from 'v3/components/Button';
import { usePermission } from 'hooks';
import { formatDateTime } from '../../../../utils/formatter';
import { useRoute } from '../shared/context';
import { validate } from '../shared/validator';
import { fetchDistanceInKilometers } from '../shared/utils';
import SharedData from '../shared/Section';
import Inputs from '../shared/Route';
import Editable from '../shared/Editable';
import StatusColor from '../shared/RouteStatus';
import polyline from '@mapbox/polyline';
import * as Styled from '../shared/styles';

const isValidCoordinate = (value) => typeof value === 'number' && !Number.isNaN(value);

const buildViaPoint = (waypoint, index) => ({
  identifier: `via-${index}-${Number(waypoint.lat).toFixed(6)}-${Number(waypoint.lng).toFixed(6)}`,
  type: { id: 'via', name: 'Ponto intermediário' },
  action: { id: 'via', name: 'Ponto intermediário' },
  lat: waypoint.lat,
  lng: waypoint.lng,
  formatted_address: '',
  address: '',
});

function buildWaypointsFromPolyline(savedPolyline, businessPoints = []) {
  if (!savedPolyline || typeof savedPolyline !== 'string' || businessPoints.length < 2) {
    return [];
  }

  try {
    const decoded = polyline
      .decode(savedPolyline)
      .map(([lat, lng]) => ({ lat, lng }))
      .filter((point) => isValidCoordinate(point.lat) && isValidCoordinate(point.lng));

    if (decoded.length < 3) {
      return [];
    }

    const internalPoints = decoded.slice(1, -1);
    const sourceIndex = Math.round((internalPoints.length - 1) / 2);

    return [
      businessPoints[0],
      internalPoints[sourceIndex],
      businessPoints[businessPoints.length - 1],
    ];
  } catch {
    return [];
  }
}

function mergeRouteWaypoints(points = [], waypoints = []) {
  const validWaypoints = (waypoints || []).filter(
    (point) => isValidCoordinate(point?.lat) && isValidCoordinate(point?.lng),
  );

  if (points.length < 2 || validWaypoints.length <= points.length) {
    return points;
  }

  let businessPointIndex = 0;
  let viaPointIndex = 0;

  return validWaypoints.map((waypoint, waypointIndex) => {
    const isFirstPoint = waypointIndex === 0;
    const isLastPoint = waypointIndex === validWaypoints.length - 1;

    if (isFirstPoint || isLastPoint) {
      const point = points[businessPointIndex];
      businessPointIndex += 1;
      return point;
    }

    const viaPoint = buildViaPoint(waypoint, viaPointIndex);
    viaPointIndex += 1;
    return viaPoint;
  });
}

export default function Route({ hasPermissionToEditRoute }) {
  const route = useRoute();
  const snackbar = useSnackbar();
  const [isEditing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const params = useParams();
  const hasPermission = usePermission('USUARIO_GR');

  const getPointLetter = (index) => String.fromCharCode(65 + index);

  function normalizeCityValue(value) {
    if (!value) return value;
    let output = value;
    if (typeof output === 'object') {
      output = output.name || output.city_name || output.city || '';
    }
    if (typeof output === 'string') {
      return output.replace(/^"(.*)"$/, '$1');
    }
    return output;
  }

  useEffect(() => {
    if (!params.id)
      route.setData({
        destinations: [{ action: true }],
        origin: {},
      });

  }, [route.data.destinations]);

  useEffect(() => {
    if (!route.data.distance) {
      fetchDistanceInKilometers(route);
    }
  }, [route?.data.destinations]);

  const mapPoints = useMemo(() => {
    const points = [];
    if (route?.data?.origin?.lat && route?.data?.origin?.lng) {
      points.push({
        ...route.data.origin,
        type: { id: 'collect', name: 'Coleta' },
      });
    }

    if (route?.data?.destinations?.length > 0) {
      route.data.destinations.forEach((destination) => {
        if (destination?.lat && destination?.lng) {
          points.push({
            ...destination,
            type: {
              id: destination?.action ? 'delivery' : 'collect',
              name: destination?.action ? 'Descarga' : 'Carga',
            },
          });
        }
      });
    }

    return points;
  }, [route?.data?.origin, route?.data?.destinations]);

  const routeMachinePoints = useMemo(() => {
    const waypoints = buildWaypointsFromPolyline(route?.data?.polyline, mapPoints);

    return mergeRouteWaypoints(mapPoints, waypoints);
  }, [mapPoints, route?.data?.polyline]);

  async function saveData() {
    setLoading(true);
    try {
      const normalizedRoute = {
        ...route.data,
        origin: {
          ...route.data?.origin,
          city: normalizeCityValue(route.data?.origin?.city),
        },
        destinations: (route.data?.destinations || []).map((destination) => ({
          ...destination,
          city: normalizeCityValue(destination?.city),
        })),
      };

      const isValid = await validate('route', { route: normalizedRoute }, route.setErrors, {
        origin: normalizedRoute.origin,
        destinations: normalizedRoute.destinations,
      });

      if (!isValid) {
        snackbar.show(<Text>Verifique os campos obrigatórios</Text>, {
          type: 'error',
        });
        setLoading(false);
        return;
      }

      const payload = {
        origin: normalizedRoute?.origin,
        destinations: normalizedRoute?.destinations,
        polyline: route.data?.polyline,
        distance: route.data?.distance,
      };

      await api.put(`v3/route/${params.id}/routes`, payload);

      route.updatePrevRoute();
      snackbar.show(<Text>Salvo com sucesso</Text>, { type: 'success' });
      setEditing(false);
    } catch (error) {
      snackbar.show(<Text>{error.response?.data[0]?.message || 'Erro ao salvar dados'}</Text>, {
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }

  async function updateApprovalGR() {
    try {
      const user = JSON.parse(localStorage.getItem('usuario'));
      const payload = {
        user_gr_id: user.id,
      };

      const { data } = await api.put(`v3/route/${params.id}/approval`, payload);
      snackbar.show(<Text>Aprovado com sucesso!</Text>, { type: 'success' });

      route.data.userGr = {
        id: data.approval.user.user_gr_id,
        username: data.approval.user.username,
      };
      route.data.date_approved_gr = data.approval.date_approved_gr;

      route.updatePrevRoute();
    } catch (error) {
      snackbar.show(<Text>Erro ao atualizar aprovação,</Text>, {
        type: 'error',
      });
    }
  }

  return (
    <>
      <SharedData
        Title="Rota"
        HeaderRightComponent={
          hasPermissionToEditRoute ? (
            <Editable
              isEditing={isEditing}
              setEditing={setEditing}
              onSave={saveData}
              isLoading={loading}
            />
          ) : null
        }
        loading={route.isFetching}
      >
        {!isEditing && hasPermission ? (
            <Row
              className="approval-gr"
              style={{
                minHeight: 52,
                marginBottom: 12,
                paddingBottom: 8,
                borderBottom: '1px solid #E5EAEE',
              }}
            >
            <Col xs="auto">
              <Button
                variant="success"
                onClick={() => {
                  updateApprovalGR();
                }}
              >
                <Text weight={500} type="regular">
                  Aprovado GR
                </Text>
              </Button>
            </Col>
          </Row>
        ) : null}

        {isEditing ? (
          <Inputs />
        ) : (
          <Row>
            <Col xs={12}>
              <div
                style={{
                  border: '1px solid #E5EAEE',
                  borderRadius: 8,
                  padding: '12px 14px',
                  marginBottom: 12,
                  background: '#FAFCFE',
                }}
              >
                <div
                  style={{
                    borderBottom: '1px solid #E5EAEE',
                    marginBottom: 10,
                    paddingBottom: 8,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <span
                    style={{
                      minWidth: 24,
                      height: 24,
                      borderRadius: 12,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#E7F6EC',
                      color: '#1F8F4E',
                      fontWeight: 700,
                      fontSize: 12,
                    }}
                  >
                    {getPointLetter(0)}
                  </span>
                  <Text type="label" color="#1F2A37" weight={700}>
                    Origem
                  </Text>
                </div>

                <Text type="regular" color="gray" as="p" className="mt-2">
                  <strong>Cliente:</strong>{' '}
                  {(
                    route.data?.origin?.client?.label
                      ? route.data?.origin?.client?.label
                      : 'Não informado'
                  )?.toUpperCase()}
                </Text>

                <Text type="regular" color="gray" as="p" className="mt-2">
                  {(
                    route.data?.origin ? formatAddress(route.data?.origin) : 'Não informado'
                  )?.toUpperCase()}
                </Text>
              </div>
            </Col>
            {route.data?.destinations?.map((_, index) => {
              const isFinalDestination = route.data.destinations.length - 1 === index;
              return (
                <Col xs={12} key={`destination-${index}`}>
                  <div
                    style={{
                      border: '1px solid #E5EAEE',
                      borderRadius: 8,
                      padding: '12px 14px',
                      marginBottom: 12,
                      background: isFinalDestination ? '#FFF9F3' : '#FFFFFF',
                    }}
                  >
                    <div
                      style={{
                        borderBottom: '1px solid #E5EAEE',
                        marginBottom: 10,
                        paddingBottom: 8,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      <span
                        style={{
                          minWidth: 24,
                          height: 24,
                          borderRadius: 12,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: isFinalDestination ? '#FFF3E8' : '#EAF1FF',
                          color: isFinalDestination ? '#C8681A' : '#2E6FD8',
                          fontWeight: 700,
                          fontSize: 12,
                        }}
                      >
                        {getPointLetter(index + 1)}
                      </span>
                      <Text
                        type="label"
                        color={isFinalDestination ? '#8A5A00' : '#1F2A37'}
                        weight={700}
                      >
                        {isFinalDestination ? 'Destino Final' : `Destino ${index + 1}`}
                      </Text>
                    </div>

                    <Text type="regular" color="gray" as="p" className="mt-2">
                      <strong>Cliente:</strong>{' '}
                      {(route.data?.destinations[index]?.client?.label || 'Não informado')?.toUpperCase()}
                    </Text>

                    <Text type="regular" color="gray" as="p" className="mt-2">
                      {(formatAddress(route.data?.destinations[index]) || 'Não informado')?.toUpperCase()}
                    </Text>

                    <Text type="medium" color="gray" as="p" className="mt-0">
                      <strong>Ação:</strong>{' '}
                      {route.data?.destinations[index].action ? 'Descarga' : 'Carga' || 'Não informado'}
                    </Text>
                  </div>
                </Col>
              );
            })}

            <Col xs={6}>
              <Text type="label" color="#464E5F">
                Distância (Km)
              </Text>
              <Text type="regular" color="gray" as="p" className="mt-2">
                {`${formatNumber(route?.data?.distance)} Km` || 'Não informado'}
              </Text>
            </Col>

            <Col xs={6}>
              <Text type="label" color="#464E5F">
                Aprovado por
              </Text>
              <Text type="regular" color="gray" as="p" className="mt-2">
                {route.data?.userGr?.username && route.data?.date_approved_gr ? (
                  <>
                    {route.data?.userGr?.username} em {formatDateTime(route.data?.date_approved_gr)}
                  </>
                ) : (
                  'Não aprovado'
                )}
              </Text>
            </Col>

            <Col xs={12}>
              <Text type="label" color="#464E5F">
                Mapa
              </Text>
              <Styled.RouteMapContainer>
                <RouteMachineMap
                  points={routeMachinePoints}
                  canDragPoint={() => false}
                  allowWaypointEditing={false}
                  readOnly
                  showReadOnlyOverlay
                />
              </Styled.RouteMapContainer>
              <StatusColor />
            </Col>
          </Row>
        )}
      </SharedData>
    </>
  );
}
